import type { UIElement } from "../types";

// Strip framework prefixes so tags fit in embedded-host ~80-char previews.
// "XCUIElementTypeButton" → "Button"; "android.widget.TextView" → "TextView".
export function shortType(t?: string): string {
  if (!t) return "";
  return t.replace(/^XCUIElementType/, "").replace(/^.*\./, "");
}

export function inferRole(type?: string, clickable?: boolean, editable?: boolean): string {
  const t = (type || "").toLowerCase();
  if (editable || t.includes("textfield") || t.includes("edittext") || t.includes("searchfield"))
    return "textbox";
  if (t.includes("secure")) return "textbox";
  if (t.includes("switch") || t.includes("toggle")) return "switch";
  if (t.includes("checkbox")) return "checkbox";
  if (t.includes("radiobutton") || t.includes("radio")) return "radio";
  if (
    t.includes("slider") ||
    t.includes("seekbar") ||
    t.includes("progressbar") ||
    t.includes("progressindicator")
  )
    return "slider";
  if (t.includes("link")) return "link";
  if (t.includes("tabbar") || t.includes("segmentedcontrol")) return "tablist";
  if (t.includes("tab") && !t.includes("table")) return "tab";
  if (t.includes("navigationbar")) return "banner";
  if (t.includes("toolbar")) return "toolbar";
  if (t.includes("statusbar")) return "status";
  if (t.includes("menu") || t.includes("popover")) return "menu";
  if (t.includes("menuitem")) return "menuitem";
  if (t.includes("alert") || t.includes("dialog")) return "dialog";
  if (t.includes("table") && !t.includes("tablerow") && !t.includes("tablecell")) return "list";
  if (t.includes("collectionview") || t.includes("recyclerview") || t.includes("listview"))
    return "list";
  if (t.includes("cell") || t.includes("row") || t.includes("listitem")) return "listitem";
  if (t.includes("heading") || t.includes("header")) return "heading";
  if (t.includes("scrollview")) return "region";
  if (t.includes("webview") || t.includes("browser")) return "document";
  if (t.includes("button") || clickable) return "button";
  if (t.includes("image") || t.includes("imageview") || t.includes("icon")) return "img";
  if (t.includes("statictext") || t.includes("textview") || t.includes("label")) return "text";
  return "generic";
}

/** Primary user-visible label for an element. */
export function primaryLabel(el: UIElement): string {
  return el.text || el.contentDesc || el.accessibilityId || el.value || el.type || "";
}

/**
 * Compact one-line metadata appended to `aria-label`. Kept short because
 * embedded host pickers truncate to ~80 chars.
 */
export function buildAriaLabel(el: UIElement): string {
  const label = primaryLabel(el);
  const meta: string[] = [];
  const t = shortType(el.type);
  if (t) meta.push(t);
  if (el.value) meta.push(`v=${el.value}`);
  if (el.accessibilityId && el.accessibilityId !== label) meta.push(`id=${el.accessibilityId}`);
  if (el.contentDesc && el.contentDesc !== label) meta.push(`desc=${el.contentDesc}`);
  const flags: string[] = [];
  if (el.clickable) flags.push("clk");
  if (el.editable) flags.push("edt");
  if (el.scrollable) flags.push("scr");
  if (el.focused) flags.push("foc");
  if (flags.length) meta.push(flags.join(""));
  meta.push(`@${el.x},${el.y} ${el.width}x${el.height}`);
  return label ? `${label} [${meta.join(" ")}]` : `[${meta.join(" ")}]`;
}

interface Tree {
  byIndex: Map<number, UIElement>;
  childrenOf: Map<number, UIElement[]>;
}

function buildTree(elements: UIElement[]): Tree {
  const byIndex = new Map<number, UIElement>();
  const childrenOf = new Map<number, UIElement[]>();
  for (const el of elements) {
    byIndex.set(el.index, el);
    if (el.parentIndex != null) {
      const arr = childrenOf.get(el.parentIndex) ?? [];
      arr.push(el);
      childrenOf.set(el.parentIndex, arr);
    }
  }
  return { byIndex, childrenOf };
}

function sameBoundsAsParent(el: UIElement, byIndex: Map<number, UIElement>): boolean {
  const p = el.parentIndex != null ? byIndex.get(el.parentIndex) : undefined;
  return !!(p && el.x === p.x && el.y === p.y && el.width === p.width && el.height === p.height);
}

function outsideParent(el: UIElement, byIndex: Map<number, UIElement>): boolean {
  const p = el.parentIndex != null ? byIndex.get(el.parentIndex) : undefined;
  if (!p) return false;
  return (
    el.x < p.x ||
    el.y < p.y ||
    el.x + el.width > p.x + p.width ||
    el.y + el.height > p.y + p.height
  );
}

function isEmptyOther(el: UIElement): boolean {
  if (el.clickable || el.editable || el.scrollable || el.focused) return false;
  if (el.text || el.contentDesc || el.accessibilityId || el.value) return false;
  const t = (el.type || "").toLowerCase();
  return (
    t.includes("other") ||
    t.includes("group") ||
    t.includes("generic") ||
    t.includes("viewgroup") ||
    t.includes("framelayout") ||
    t.includes("linearlayout") ||
    t.includes("relativelayout") ||
    t.includes("constraintlayout") ||
    t === ""
  );
}

function contains(outer: UIElement, inner: UIElement): boolean {
  return (
    outer.x <= inner.x &&
    outer.y <= inner.y &&
    outer.x + outer.width >= inner.x + inner.width &&
    outer.y + outer.height >= inner.y + inner.height
  );
}

/**
 * iOS roots — `XCUIElementTypeApplication` and `XCUIElementTypeWindow` —
 * are never intended tap targets. They cover the whole screen (sometimes
 * with swapped / overflowing bounds from the a11y backend) and only get
 * in the way of a picker. Drop them regardless of label or children.
 */
function isIOSRoot(el: UIElement): boolean {
  const t = (el.type || "").toLowerCase();
  return t.includes("application") || t.includes("window");
}

/**
 * Drop an empty/unlabeled `Other` leaf when its bounds cover another
 * element in the tree. Keeping it would add a competing hit zone on top
 * of the real target; dropping it never loses information because every
 * element it covers is still reachable.
 *
 * Only applies to leaves (no children in the tree): non-leaves have kids
 * that represent them, while non-Other types (Button, Image, Text…) may
 * be the real target even without labels, so we leave them alone.
 */
function coversOthers(el: UIElement, hasChildren: boolean, elements: UIElement[]): boolean {
  if (hasChildren) return false;
  if (!isEmptyOther(el)) return false;
  for (const o of elements) {
    if (o.index === el.index) continue;
    if (o.width === 0 || o.height === 0) continue;
    if (contains(el, o)) return true;
  }
  return false;
}

function isAncestorOf(
  ancestorIdx: number,
  descendantIdx: number,
  byIndex: Map<number, UIElement>,
): boolean {
  let cur = byIndex.get(descendantIdx);
  while (cur && cur.parentIndex != null) {
    if (cur.parentIndex === ancestorIdx) return true;
    cur = byIndex.get(cur.parentIndex);
  }
  return false;
}

/** Filter out accessibility wrappers and out-of-parent junk, then sort so
 *  the embedded-browser picker hits the most specific element. */
export function filterAndSort(elements: UIElement[]): UIElement[] {
  const { byIndex, childrenOf } = buildTree(elements);
  return [...elements]
    .filter(
      (el) =>
        el &&
        el.width > 0 &&
        el.height > 0 &&
        !isIOSRoot(el) &&
        !sameBoundsAsParent(el, byIndex) &&
        !outsideParent(el, byIndex) &&
        !coversOthers(el, (childrenOf.get(el.index)?.length ?? 0) > 0, elements),
    )
    .sort((a, b) => {
      if (isAncestorOf(a.index, b.index, byIndex)) return -1;
      if (isAncestorOf(b.index, a.index, byIndex)) return 1;
      const da = a.width * a.height;
      const db = b.width * b.height;
      if (da !== db) return db - da;
      const ca = a.clickable ? 1 : 0;
      const cb = b.clickable ? 1 : 0;
      if (ca !== cb) return ca - cb;
      return a.index - b.index;
    });
}

/** Flatten descendant labels into terse lines, for embedding in the
 *  parent element's textContent so host pickers grab child text too. */
export function descendantLines(
  elements: UIElement[],
  rootIdx: number,
  maxDepth = 4,
  maxCount = 30,
): string[] {
  const { childrenOf } = buildTree(elements);
  const out: string[] = [];
  const stack: { idx: number; depth: number }[] = [{ idx: rootIdx, depth: 1 }];
  while (stack.length && out.length < maxCount) {
    const { idx, depth } = stack.pop()!;
    const kids = childrenOf.get(idx) || [];
    for (let i = kids.length - 1; i >= 0; i--) {
      if (depth <= maxDepth) stack.push({ idx: kids[i].index, depth: depth + 1 });
    }
    for (const k of kids) {
      const lbl = k.text || k.contentDesc || k.accessibilityId || k.value;
      if (!lbl) continue;
      const tag = shortType(k.type);
      const v = k.value && k.value !== lbl ? `=${k.value}` : "";
      out.push(`↳${lbl}${v}${tag ? "·" + tag : ""}`);
      if (out.length >= maxCount) break;
    }
  }
  return out;
}
